import type { HTMLAttributes } from "react";

import { useI18n } from "../use-i18n";
import { cn, cv, type VariantProps } from "../utils";

const LoadingLogo = () => {
  return (
    <svg
      viewBox="0 0 300 272"
      role="presentation"
      fill="currentColor"
      className="text-brand w-12 opacity-100 transition-opacity duration-500 starting:opacity-0"
    >
      <path
        className="animate-loading-logo-pulse origin-center will-change-transform"
        d="M94.6077 0H15.7155C7.05649 0 0 7.12582 0 15.9001V26.4667C0 35.3282 7.12541 42.5456 15.9008 42.5456H94.6077C103.357 42.5456 110.491 35.3282 110.491 26.4667V16.0789C110.491 7.21741 103.357 0 94.6077 0Z"
      />
      <path
        className="animate-loading-logo-pulse origin-center will-change-transform"
        d="M282.608 0H203.901C195.125 0 188 7.21741 188 16.0963V26.7066C188 35.4372 195.056 42.5674 203.716 42.5674H282.608C291.357 42.5674 298.491 35.3457 298.491 26.4667V16.0963C298.491 7.21741 291.357 0 282.608 0Z"
      />
      <path
        className="animate-loading-logo-pulse origin-center will-change-transform"
        d="M94.6077 229H15.7155C7.05648 229 0 236.126 0 244.9V255.467C0 264.328 7.12541 271.546 15.9008 271.546H94.6077C103.357 271.546 110.491 264.328 110.491 255.467V245.079C110.491 236.217 103.357 229 94.6077 229Z"
      />
      <path
        className="animate-loading-logo-pulse origin-center will-change-transform"
        d="M282.608 229H203.716C195.056 229 188 236.126 188 244.9V255.467C188 264.328 195.125 271.546 203.901 271.546H282.608C291.357 271.546 298.491 264.328 298.491 255.467V245.079C298.491 236.217 291.357 229 282.608 229Z"
      />
      <path
        className="animate-loading-logo-rotate origin-center will-change-transform"
        d="M256.832 141.329V130.941C256.832 128.839 256.47 126.798 255.639 124.827C253.187 118.717 247.458 114.801 240.944 114.801H171.835V43.7305C171.835 34.9998 164.525 27.6603 155.956 27.6603H142.481C133.839 27.6603 126.584 34.9998 126.584 43.7305V114.801H57.4366C48.6354 114.801 41.5013 122.019 41.5013 130.88V141.272C41.5013 143.418 41.9321 145.502 42.686 147.347C45.1502 153.448 50.9143 157.408 57.4366 157.408H126.584V228.474C126.584 237.174 133.839 244.557 142.481 244.557H155.956C164.525 244.557 171.835 237.174 171.835 228.474V157.408H240.944C249.732 157.408 256.832 150.182 256.832 141.329Z"
      />
    </svg>
  );
};

const LoadingDots = () => {
  return (
    <svg
      viewBox="0 0 50 20"
      role="presentation"
      fill="currentColor"
      className="w-[2em] animate-[fade-in_0.5s_0.25s_forwards]"
    >
      <circle className="animate-loading-pulse" r="15%" cx="20%" cy="50%" />
      <circle className="animate-loading-pulse [animation-delay:0.4s]" r="15%" cx="50%" cy="50%" />
      <circle className="animate-loading-pulse [animation-delay:0.8s]" r="15%" cx="80%" cy="50%" />
    </svg>
  );
};

const ncLoadingIndicatorVariants = cv("", {
  variants: {
    variant: {
      default: "inline-flex",
      logo: "flex items-center justify-center h-full",
    },
  },
  defaultVariants: {
    variant: "logo",
  },
});

export interface NcLoadingIndicatorProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof ncLoadingIndicatorVariants> {}

export const NcLoadingIndicator = ({
  variant = "default",
  className,
  ...props
}: NcLoadingIndicatorProps) => {
  const { t } = useI18n();
  return (
    <span
      data-nc="NcLoadingIndicator"
      className={cn(ncLoadingIndicatorVariants({ variant }), className)}
      aria-live="polite"
      {...props}
    >
      <span className="sr-only">{t("loading")}</span>
      {variant === "logo" ? <LoadingLogo /> : <LoadingDots />}
    </span>
  );
};
